/**
 * Quadro Kanban genérico (Mercado Livre, funis). Colunas com título, dica e
 * contagem; cada cartão é um nó React fornecido pelo pai. Rola na horizontal
 * em telas estreitas. Sem drag-and-drop: a mudança de etapa é por botão no
 * cartão (acessível por teclado e coberta por teste SSR).
 */
import { createElement as h, type ReactElement, type ReactNode } from "react";

export interface KanbanColumn {
  id: string;
  title: string;
  hint?: string;
  cards: Array<{ id: string; node: ReactNode }>;
}

export interface KanbanBoardProps {
  label: string;
  columns: KanbanColumn[];
  emptyColumnText?: string;
}

export function KanbanBoard({ label, columns, emptyColumnText = "Nenhum pedido" }: KanbanBoardProps): ReactElement {
  return h(
    "div",
    { role: "list", "aria-label": label, "data-kanban": "board", className: "flex gap-3 overflow-x-auto pb-2" },
    columns.map((column) =>
      h(
        "section",
        {
          key: column.id,
          role: "listitem",
          "aria-label": `${column.title}, ${column.cards.length} pedidos`,
          "data-kanban-column": column.id,
          className: "flex w-72 shrink-0 flex-col gap-2 rounded-panel border border-border bg-surface-soft p-3"
        },
        h(
          "div",
          { className: "flex items-start justify-between gap-2" },
          h(
            "div",
            { className: "min-w-0" },
            h("h3", { className: "text-sm font-extrabold text-ink" }, column.title),
            column.hint ? h("p", { className: "text-xs text-muted" }, column.hint) : null
          ),
          h(
            "span",
            { className: "rounded-pill bg-surface px-2 py-0.5 text-xs font-extrabold text-body" },
            String(column.cards.length)
          )
        ),
        column.cards.length === 0
          ? h(
              "p",
              { className: "rounded-control border border-dashed border-border-strong p-3 text-center text-xs text-muted" },
              emptyColumnText
            )
          : column.cards.map((card) => h("div", { key: card.id, "data-kanban-card": card.id }, card.node))
      )
    )
  );
}
